import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import React from 'react';
import { toast } from 'react-hot-toast';

import Coordinates from '../../containers/SellerApp/SaleShow/pages/Mandate/steps/Coordinates';
import Situation from '../../containers/SellerApp/SaleShow/pages/Mandate/steps/Situation';
import Signature from '../../containers/SellerApp/SaleShow/pages/Mandate/steps/Signature';

import USER from '../../api/ressources/SellerApp/user';
import SALES from '../../api/ressources/SellerApp/sales';
import MANDATES from '../../api/ressources/SellerApp/mandates';

export const submitCoordinates = createAsyncThunk(
  '/submitCoordinates',
  async (saleId, { getState, rejectWithValue }) => {
    const mandateStore = getState().sellerMandates
    const user = mandateStore.onboardedUser;
    const { email, ...userStateWithoutEmail } = user

    try {
      const results = await Promise.allSettled([
        USER.updateUser(userStateWithoutEmail),
        SALES.updateSale(saleId, { buying_year: mandateStore.saleBuyingYear })
      ]);

      const errors = results
        .filter(result => result.status === 'rejected')
        .map(result => result.reason.response.data.error.message)
        .reduce((acc, current) => {
          return { ...acc, ...current };
        }, {});

      if (Object.keys(errors).length > 0) {
        return rejectWithValue({ errors: errors });
      }

      return {
        user: results[0].value.data,
        sale: results[1].value.data
      };
    } catch (error) {
      return rejectWithValue({
        message: 'Une erreur inattendue est survenue',
        error: error
      });
    }
  }
);

export const getMandate = createAsyncThunk(
  '/getMandate',
  async (mandateId) => {
    const response = await MANDATES.getMandate(mandateId)
    return response.data
  }
);

export const updateMandate = createAsyncThunk(
  '/updateMandate',
  async ({ mandateId, params }) => {
    const response = await MANDATES.updateMandate(mandateId, params)
    return response.data
  }
);

export const genereteMandate = createAsyncThunk(
  '/genereteMandate',
  async (mandateId) => {    
    const response = await MANDATES.genereteMandate(mandateId)
    return response.data
  }
);

const allSteps = [
  { number: 1, name: 'Coordonnées', component: Coordinates },
  { number: 2, name: 'Situation', component: Situation },
  { number: 3, name: 'Signature', component: Signature }
]

export const initialState = {
  steps: allSteps,
  currentStep: allSteps[0],
  onboardedUser: {},
  saleBuyingYear: '',
  mandateRequestState: '',
  mandate: {},
  isSubmitting: false,
  isGenerating: false,
  yousignStatus: '',
  errors: {},
  stateReady: false
};

export const mandatesSlice = createSlice({
  name: 'mandates',
  initialState,
  reducers: {
    resetMandate: (state) => {
     state.mandate = initialState.mandate
    },
    updateCurrentStep: (state, { payload }) => {
     state.currentStep = state.steps.find(step => step.number === payload)
     window.scrollTo({ top: 0 });
    },
    updateOnboardedUser: (state, { payload }) => {
      state.onboardedUser = payload
      state.stateReady = true
    },
    updateSaleBuyingYear: (state, { payload }) => {
      state.saleBuyingYear = payload
    },
    updateErrors: (state, { payload }) => {
      state.errors = payload
    },
    updateYousignStatus: (state, { payload }) => {
      state.yousignStatus = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(submitCoordinates.pending, (state) => {
      state.isSubmitting = true
    })
    builder.addCase(submitCoordinates.fulfilled, (state) => {
      state.currentStep = state.steps.find(step => step.number === state.currentStep.number + 1)
      state.isSubmitting = false
      window.scrollTo({ top: 0 });
    })
    builder.addCase(submitCoordinates.rejected, (state, { payload }) => {
      state.errors = payload.errors;
      state.isSubmitting = false
    })
    builder.addCase(getMandate.fulfilled, (state, { payload }) => {
      state.mandate = payload;
      state.mandateRequestState = 'done';
    })
    builder.addCase(updateMandate.pending, (state) => {
      state.isSubmitting = true;
    })
    builder.addCase(updateMandate.fulfilled, (state, { payload }) => {      
      state.mandate = payload;
      state.isSubmitting = false;
    })
    builder.addCase(genereteMandate.pending, (state) => {
      state.isGenerating = true
    })
    builder.addCase(genereteMandate.fulfilled, (state, { payload }) => {
      state.mandate = payload;
      state.isGenerating = false;
    })
    builder.addCase(genereteMandate.rejected, (state) => {
      toast.error("Une erreur s'est produite, veuillez réessayer plus tard où contacter votre expert", { duration: 3000 })
      state.isGenerating = false;
    })
  },
})

export const { resetMandate, updateCurrentStep, updateOnboardedUser, updateSaleBuyingYear, updateErrors, updateYousignStatus } = mandatesSlice.actions;

export default mandatesSlice.reducer;
